import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { requestOrDelete } from "@/lib/approval";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton, EmptyState } from "@/components/ui-kit";
import { Truck, ArrowUpRight, MagnifyingGlass, DownloadSimple, Printer, X } from "@phosphor-icons/react";
import { VEHICLE_TYPE_OPTIONS } from "@/lib/vehicleLayouts";
import { downloadCsv, printElement, regSort } from "@/lib/csv";

const TYPES = ["truck", "van", "bus", "car", "tractor"];
const FUELS = ["diesel", "petrol", "cng", "electric", "hybrid"];
const STATUSES = ["active", "in_service", "idle", "decommissioned", "sold"];

const empty = {
    reg_number: "", make: "", model: "", type: "truck", fuel_type: "diesel",
    gvw_kg: "", vehicle_type_code: "truck_6", year: new Date().getFullYear(),
    odometer: 0, location_id: "", status: "active", fixed_mileage_kmpl: "",
};

export default function Vehicles() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [locations, setLocations] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);

    // Filters
    const [search, setSearch] = useState("");
    const [filterMake, setFilterMake] = useState("");
    const [filterModel, setFilterModel] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [showRegSuggest, setShowRegSuggest] = useState(false);
    const tableRef = useRef(null);

    const load = useCallback(() => {
        // Always fetch sold vehicles too — the Sold filter is client-side so
        // the user can flip to it without a network round trip.
        return api.get("/vehicles?include_sold=true").then((r) => setList(r.data));
    }, []);
    useEffect(() => {
        load();
        api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    }, [load]);

    // Build option lists from current dataset (make/model/year)
    const makes = useMemo(
        () => Array.from(new Set(list.map((v) => v.make).filter(Boolean))).sort(),
        [list]
    );
    const models = useMemo(() => {
        const filteredByMake = filterMake ? list.filter((v) => v.make === filterMake) : list;
        return Array.from(new Set(filteredByMake.map((v) => v.model).filter(Boolean)))
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
    }, [list, filterMake]);
    const years = useMemo(
        () => Array.from(new Set(list.map((v) => v.year).filter(Boolean))).sort((a, b) => b - a),
        [list]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return list.filter((v) => {
            if (q && !(v.reg_number || "").toLowerCase().includes(q)) return false;
            if (filterMake && v.make !== filterMake) return false;
            if (filterModel && v.model !== filterModel) return false;
            if (filterYear && String(v.year) !== String(filterYear)) return false;
            if (filterStatus && v.status !== filterStatus) return false;
            // Hide sold vehicles from the default view unless the user picks Sold explicitly
            if (!filterStatus && v.status === "sold") return false;
            return true;
        });
    }, [list, search, filterMake, filterModel, filterYear, filterStatus]);

    // Reg-number suggestions (top 8 matches)
    const regSuggestions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return [];
        return list
            .filter((v) => (v.reg_number || "").toLowerCase().includes(q))
            .slice(0, 8);
    }, [list, search]);

    const clearFilters = () => {
        setSearch(""); setFilterMake(""); setFilterModel("");
        setFilterYear(""); setFilterStatus("");
    };
    const hasFilters = search || filterMake || filterModel || filterYear || filterStatus;

    const exportCsv = () => {
        downloadCsv(`vehicles-${new Date().toISOString().slice(0, 10)}.csv`, filtered, [
            ["REG #", "reg_number"],
            ["MAKE", "make"],
            ["MODEL", "model"],
            ["YEAR", "year"],
            ["TYPE", "type"],
            ["FUEL", "fuel_type"],
            ["GVW (KG)", "gvw_kg"],
            ["FIXED MILEAGE (KM/L)", "fixed_mileage_kmpl"],
            ["ODOMETER (KM)", "odometer"],
            ["STATUS", "status"],
            ["LOCATION", (v) => locations.find((l) => l.id === v.location_id)?.name || ""],
        ]);
    };

    const exportPdf = () => {
        printElement(tableRef.current, `EAZYFLEET · Vehicles · ${filtered.length} record(s)`);
    };

    const save = async () => {
        try {
            const payload = {
                ...form,
                year: Number(form.year) || null,
                odometer: Number(form.odometer) || 0,
                location_id: form.location_id || null,
                fixed_mileage_kmpl: form.fixed_mileage_kmpl === "" || form.fixed_mileage_kmpl == null
                    ? null
                    : Number(form.fixed_mileage_kmpl),
            };
            if (editing) await api.put(`/vehicles/${editing}`, payload);
            else await api.post("/vehicles", payload);
            toast.success(editing ? "Vehicle updated" : "Vehicle added");
            setOpen(false); setEditing(null); setForm(empty); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    };

    const edit = (v) => { setForm({ ...empty, ...v, location_id: v.location_id || "" }); setEditing(v.id); setOpen(true); };
    const del = async (v) => {
        const r = await requestOrDelete("vehicle", v.id, `vehicle ${v.reg_number}`, user?.role);
        if (r.ok && !r.requested) load();
    };

    // ---------- Sell vehicle ----------
    const emptySale = { buyer_name: "", sale_date: new Date().toISOString().slice(0, 10), sale_amount_inr: "", buyer_phone: "", buyer_address: "", notes: "" };
    const [sellFor, setSellFor] = useState(null);
    const [sellForm, setSellForm] = useState(emptySale);
    const [sellSubmitting, setSellSubmitting] = useState(false);
    const openSell = (v) => { setSellFor(v); setSellForm(emptySale); };
    const submitSell = async () => {
        if (!sellFor) return;
        if (!sellForm.buyer_name.trim()) { toast.error("Buyer name is required"); return; }
        const amt = Number(sellForm.sale_amount_inr);
        if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a valid sale amount"); return; }
        setSellSubmitting(true);
        const isAdmin = user?.role === "admin";
        const saleInfo = {
            buyer_name: sellForm.buyer_name.trim(),
            sale_date: sellForm.sale_date || null,
            sale_amount_inr: amt,
            buyer_phone: sellForm.buyer_phone.trim() || null,
            buyer_address: sellForm.buyer_address.trim() || null,
            notes: sellForm.notes.trim() || null,
        };
        try {
            if (isAdmin) {
                await api.post(`/vehicles/${sellFor.id}/sell`, saleInfo);
                toast.success(`Vehicle sold to ${sellForm.buyer_name.trim()}. Its tyres/batteries have been detached.`);
            } else {
                await api.post(`/approvals`, {
                    entity_type: "vehicle",
                    entity_id: sellFor.id,
                    action: "sell",
                    reason: sellForm.notes.trim() || null,
                    sale_info: saleInfo,
                });
                toast.success("Sale request submitted for admin approval.");
            }
            setSellFor(null); load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail, e) || "Sale failed");
        } finally { setSellSubmitting(false); }
    };
    const undoSale = async (v) => {
        if (!window.confirm(`Undo sale of ${v.reg_number}? Note: previously detached tyres/batteries will NOT be re-attached automatically.`)) return;
        try {
            await api.post(`/vehicles/${v.id}/undo-sale`);
            toast.success("Sale reversed"); load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail, e) || "Undo failed");
        }
    };

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="FLEET MANAGEMENT"
                title="Vehicles"
                subtitle={`${list.length} vehicles · master record + documents`}
                action={<NewButton onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} label="Add vehicle" testid="add-vehicle-btn" />}
            />

            {list.length === 0 ? (
                <Surface className="overflow-hidden">
                    <EmptyState
                        icon={Truck}
                        title="No vehicles yet"
                        description="Add your first vehicle to start tracking maintenance, tyres and consumables."
                        action={<NewButton onClick={() => setOpen(true)} label="Add vehicle" />}
                    />
                </Surface>
            ) : (
                <>
                    {/* Search + filter bar */}
                    <Surface className="px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[260px] max-w-md">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fleet-inkMuted" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setShowRegSuggest(true); }}
                                onFocus={() => setShowRegSuggest(true)}
                                onBlur={() => setTimeout(() => setShowRegSuggest(false), 150)}
                                placeholder="Search by registration number…"
                                className="w-full pl-9 pr-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                                data-testid="vehicle-search-input"
                            />
                            {showRegSuggest && regSuggestions.length > 0 && (
                                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-fleet-border rounded-md max-h-72 overflow-y-auto" data-testid="reg-suggest">
                                    {regSuggestions.map((v) => (
                                        <button
                                            key={v.id}
                                            type="button"
                                            onMouseDown={() => { setSearch(v.reg_number); setShowRegSuggest(false); }}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between"
                                            data-testid={`reg-suggest-${v.id}`}
                                        >
                                            <span className="font-mono font-medium">{v.reg_number}</span>
                                            <span className="text-xs text-fleet-inkSoft">{v.make} {v.model}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <select
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink min-w-[160px]"
                            data-testid="vehicle-reg-dropdown"
                        >
                            <option value="">— pick reg # —</option>
                            {[...list].sort(regSort).map((v) => (
                                <option key={v.id} value={v.reg_number}>{v.reg_number}</option>
                            ))}
                        </select>

                        <select
                            value={filterMake}
                            onChange={(e) => { setFilterMake(e.target.value); setFilterModel(""); }}
                            className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                            data-testid="vehicle-filter-make"
                        >
                            <option value="">All makes</option>
                            {makes.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>

                        <select
                            value={filterModel}
                            onChange={(e) => setFilterModel(e.target.value)}
                            className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink disabled:bg-slate-50 disabled:text-fleet-inkMuted"
                            data-testid="vehicle-filter-model"
                            disabled={models.length === 0}
                        >
                            <option value="">All models</option>
                            {models.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>

                        <select
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                            className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                            data-testid="vehicle-filter-year"
                        >
                            <option value="">All years</option>
                            {years.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>

                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                            data-testid="vehicle-filter-status"
                        >
                            <option value="">All statuses</option>
                            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                        </select>

                        {hasFilters && (
                            <button onClick={clearFilters} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline flex items-center gap-1" data-testid="clear-filters">
                                <X size={12} /> Clear
                            </button>
                        )}

                        <span className="text-xs text-fleet-inkSoft ml-auto">{filtered.length} of {list.length}</span>
                        <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="vehicle-export-csv">
                            <DownloadSimple size={14} /> CSV
                        </Button>
                        <Button variant="secondary" size="sm" onClick={exportPdf} data-testid="vehicle-export-pdf">
                            <Printer size={14} /> PDF
                        </Button>
                    </Surface>

                <Surface className="overflow-hidden">
                    <table className="w-full text-sm" ref={tableRef}>
                        <thead className="bg-slate-50">
                            <tr className="text-left">
                                {["REG #", "MAKE / MODEL", "TYPE", "FUEL", "ODOMETER", "STATUS", ""].map((h, i) => (
                                    <th key={h} className={`label-mono px-5 py-3 font-normal${i === 6 ? " no-print" : ""}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((v) => (
                                <tr key={v.id} className="border-t border-fleet-border data-grid-row" data-testid={`vehicle-row-${v.id}`}>
                                    <td className="px-5 py-3 font-mono font-medium">{v.reg_number}</td>
                                    <td className="px-5 py-3">{v.make} {v.model} <span className="text-fleet-inkMuted text-xs">{v.year || ""}</span></td>
                                    <td className="px-5 py-3 capitalize">{v.type}</td>
                                    <td className="px-5 py-3 capitalize">{v.fuel_type}</td>
                                    <td className="px-5 py-3 font-mono">{(v.odometer || 0).toLocaleString()} km</td>
                                    <td className="px-5 py-3"><Badge variant={v.status}>{v.status?.replace("_", " ")}</Badge></td>
                                    <td className="px-5 py-3 text-right no-print">
                                        <div className="inline-flex gap-1">
                                            <Link to={`/vehicles/${v.id}`} className="p-1.5 text-fleet-inkSoft hover:text-fleet-ink" data-testid={`view-vehicle-${v.id}`}><ArrowUpRight size={16} /></Link>
                                            {v.status !== "sold" && <Button variant="ghost" size="sm" onClick={() => openSell(v)} data-testid={`sell-vehicle-${v.id}`}>{user?.role === "admin" ? "Sell" : "Request sale"}</Button>}
                                            {v.status === "sold" && <Button variant="ghost" size="sm" onClick={() => undoSale(v)} data-testid={`undo-sale-vehicle-${v.id}`}>Undo sale</Button>}
                                            {v.status !== "sold" && <Button variant="ghost" size="sm" onClick={() => edit(v)} data-testid={`edit-vehicle-${v.id}`}>Edit</Button>}
                                            {v.status !== "sold" && <Button variant="ghost" size="sm" onClick={() => del(v)} data-testid={`del-vehicle-${v.id}`}>Delete</Button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">No vehicles match the current filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                </Surface>
                </>
            )}

            <Modal
                open={open} onClose={() => { setOpen(false); setEditing(null); }}
                title={editing ? "Edit vehicle" : "Add vehicle"}
                footer={<>
                    <Button variant="secondary" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
                    <Button onClick={save} data-testid="save-vehicle">Save</Button>
                </>}
            >
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Registration #" value={form.reg_number} onChange={(e) => setForm({ ...form, reg_number: e.target.value.toUpperCase() })} data-testid="vehicle-reg" />
                    <Input label="Year" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
                    <Input label="Make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
                    <Input label="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
                    <Select label="Category" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} options={TYPES} />
                    <Select label="Fuel" value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} options={FUELS} />
                    <Select label="Vehicle type (tyre layout)" className="col-span-2"
                        value={form.vehicle_type_code}
                        onChange={(e) => setForm({ ...form, vehicle_type_code: e.target.value })}
                        options={VEHICLE_TYPE_OPTIONS}
                        data-testid="vehicle-type-code" />
                    <Input label="GVW (kgs)" type="number" value={form.gvw_kg} onChange={(e) => setForm({ ...form, gvw_kg: e.target.value })} placeholder="e.g. 19000" data-testid="vehicle-gvw" />
                    <Input label="Fixed mileage (Km/L)" type="number" step="0.1"
                        value={form.fixed_mileage_kmpl}
                        onChange={(e) => setForm({ ...form, fixed_mileage_kmpl: e.target.value })}
                        placeholder="e.g. 4.5"
                        data-testid="vehicle-fixed-mileage" />
                    <Input label="Odometer (km)" type="number" value={form.odometer} onChange={(e) => setForm({ ...form, odometer: e.target.value })} />
                    <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUSES} />
                    <Select
                        label="Location"
                        value={form.location_id}
                        onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                        options={[{ value: "", label: "— none —" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
                    />
                </div>
            </Modal>

            <Modal
                open={!!sellFor}
                onClose={() => setSellFor(null)}
                title={sellFor ? `${user?.role === "admin" ? "Sell" : "Request sale of"} vehicle · ${sellFor.reg_number}` : "Sell vehicle"}
                size="md"
                footer={<>
                    <Button variant="secondary" onClick={() => setSellFor(null)} disabled={sellSubmitting}>Cancel</Button>
                    <Button onClick={submitSell} disabled={sellSubmitting || !sellForm.buyer_name.trim() || !sellForm.sale_amount_inr} data-testid="sell-vehicle-submit">
                        {sellSubmitting ? "Submitting…" : (user?.role === "admin" ? "Record sale" : "Submit request")}
                    </Button>
                </>}
            >
                {sellFor && (
                    <div className="space-y-3">
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                            {user?.role === "admin"
                                ? "Selling the vehicle will auto-detach its tyres & batteries (back to in-stock), close open fitments/assignments, un-assign drivers, and soft-delete its mileage logs so fleet reports stay clean."
                                : "This request will go to an admin. The vehicle stays as-is until they approve; then the cascade below runs automatically."}
                        </div>
                        <Input label="Buyer name *" value={sellForm.buyer_name} onChange={(e) => setSellForm({ ...sellForm, buyer_name: e.target.value })} data-testid="sell-vehicle-buyer-name" />
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Sale date" type="date" value={sellForm.sale_date} onChange={(e) => setSellForm({ ...sellForm, sale_date: e.target.value })} />
                            <Input label="Sale amount (₹) *" type="number" min="0" step="0.01" value={sellForm.sale_amount_inr} onChange={(e) => setSellForm({ ...sellForm, sale_amount_inr: e.target.value })} data-testid="sell-vehicle-amount" />
                        </div>
                        <Input label="Buyer phone (optional)" value={sellForm.buyer_phone} onChange={(e) => setSellForm({ ...sellForm, buyer_phone: e.target.value })} />
                        <div>
                            <label className="label-mono text-xs text-fleet-inkSoft mb-1 block">Buyer address (optional)</label>
                            <textarea rows={2} value={sellForm.buyer_address} onChange={(e) => setSellForm({ ...sellForm, buyer_address: e.target.value })} className="w-full border border-fleet-border rounded-md px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="label-mono text-xs text-fleet-inkSoft mb-1 block">Notes / remarks (optional)</label>
                            <textarea rows={2} value={sellForm.notes} onChange={(e) => setSellForm({ ...sellForm, notes: e.target.value })} className="w-full border border-fleet-border rounded-md px-3 py-2 text-sm" />
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
